import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { login as loginRequest, getMe } from '@/lib/api/auth'
import { ApiError } from '@/lib/api/client'
import type { UserResponse } from '@/lib/schemas/auth'

export type AuthUser = UserResponse

type AuthState = {
  user: AuthUser | null
  token: string | null
  status: 'idle' | 'hydrating' | 'ready'
  login: (
    creds: { username: string; password: string },
  ) => Promise<{ ok: true } | { ok: false; status: number; message: string }>
  logout: (opts?: { skipServerCall?: boolean }) => Promise<void>
  hydrate: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      status: 'idle',

      login: async (creds) => {
        try {
          const data = await loginRequest(creds)
          set({ token: data.access_token, user: data.user, status: 'ready' })
          return { ok: true }
        } catch (err) {
          const status = err instanceof ApiError ? err.status : 0
          const message =
            status === 401
              ? 'Invalid username or password'
              : err instanceof ApiError
                ? err.message
                : 'Login failed'
          return { ok: false, status, message }
        }
      },

      logout: async () => {
        // Server-side logout is not part of the Phase 0 auth contract; the token
        // is stateless (JWT), so clearing it client-side is sufficient. The
        // `skipServerCall` option is retained on the type for parity with the
        // reference and the api client's 401 handler.
        set({ token: null, user: null, status: 'ready' })
      },

      hydrate: async () => {
        const token = get().token
        if (!token) {
          set({ status: 'ready' })
          return
        }
        set({ status: 'hydrating' })
        try {
          const user = await getMe()
          set({ user, status: 'ready' })
        } catch {
          set({ token: null, user: null, status: 'ready' })
        }
      },
    }),
    {
      name: 'kh-auth',
      partialize: (state) => ({ token: state.token, user: state.user }),
    },
  ),
)
